import { Link } from '@tanstack/react-router';

import type { HomeTodayEvent, HomeNextSession, HomeDueItem } from '@/lib/api/home';

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function buildFallback(nextSession: HomeNextSession | null, dueItems: HomeDueItem[]): HomeTodayEvent[] {
  const out: HomeTodayEvent[] = [];
  if (nextSession?.scheduled_at) {
    out.push({ id: nextSession.id, type: 'session', title: nextSession.title || 'Live Session', time: nextSession.scheduled_at, url: nextSession.meeting_url });
  }
  // due_items now includes overdue work (the dashboard surfaces it in its own
  // group). This card only looks forward, so past deadlines are filtered out —
  // otherwise a missed item renders as "Due Mon, Jul 27".
  for (const item of dueItems.filter((i) => i.state !== 'overdue').slice(0, 2)) {
    if (item.due_date) out.push({ id: item.assessment_id, type: 'assignment', title: item.title, time: item.due_date });
  }
  return out;
}

function subtitle(ev: HomeTodayEvent): string {
  if (ev.type === 'session') return `Live Session • ${fmtTime(ev.time)}`;
  if (ev.type === 'contest') return `${dayLabel(ev.time)} • ${fmtTime(ev.time)}`;
  return `Due ${dayLabel(ev.time)} • ${fmtTime(ev.time)}`;
}

function isLiveNow(iso: string): boolean {
  const diff = new Date(iso).getTime() - Date.now();
  return diff <= 0 && diff > -2 * 60 * 60 * 1000;
}

export function BatchTodayCard({
  todayEvents,
  nextSession,
  dueItems,
}: {
  todayEvents: HomeTodayEvent[];
  nextSession: HomeNextSession | null;
  dueItems: HomeDueItem[];
}) {
  const hasToday = todayEvents.length > 0;
  const events = hasToday ? todayEvents : buildFallback(nextSession, dueItems);

  return (
    <section className="flex flex-col rounded-[20px] border border-[#ececec] bg-white p-[25px] shadow-[0px_1px_1.5px_rgba(0,0,0,0.1),0px_1px_1px_rgba(0,0,0,0.1)]">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#111827]">{hasToday ? 'Today' : 'Upcoming'}</h3>
        <Link to="/schedule" className="text-xs font-semibold text-[#8b5cf6] transition-opacity hover:opacity-75">View all</Link>
      </div>

      {events.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#9ca3af]">Nothing scheduled</p>
      ) : (
        <ul className="flex flex-col gap-5 pt-6">
          {events.map((ev, i) => {
            const live = ev.type === 'session' && isLiveNow(ev.time);
            const last = i === events.length - 1;
            return (
              <li key={`${ev.id}-${i}`} className="flex gap-4">
                <div className="flex flex-col items-center pt-1">
                  <span className={i === 0
                    ? 'size-3 shrink-0 rounded-full bg-[#ff5a1f] shadow-[0px_1px_3px_0px_#ffd6a8,0px_1px_2px_0px_#ffd6a8]'
                    : 'size-3 shrink-0 rounded-full bg-[#d1d5db]'} />
                  {!last && <span className="mt-1 h-9 w-px bg-[#ececec]" />}
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-2 text-sm font-semibold text-[#111827]">
                    <span className="truncate">{ev.title.replace(/^Session:\s*/i, '')}</span>
                    {live && (
                      <span className="shrink-0 rounded-full bg-[#fff7ed] px-2 py-0.5 text-[10px] font-bold uppercase leading-[15px] text-[#ff5a1f]">Live</span>
                    )}
                  </p>
                  <p className="pt-0.5 text-xs text-[#9ca3af]">{subtitle(ev)}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
